import React from 'react'
import { StyleSheet, View, Platform } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { IconName, Icon, Text } from '../atoms'
import { useScreen } from '@hooks'

export type HeaderProps = {
  title?: string
  iconLeft?: IconName
  iconRight?: IconName
  iconColor?: string
  onPressLeft?: () => void
  onPressRight?: () => void
  /** Custom content on the right (e.g. a text action), instead of `iconRight`. */
  right?: React.ReactNode
}

export const Header = ({
  title,
  iconLeft,
  iconRight,
  iconColor,
  onPressLeft,
  onPressRight,
  right,
}: HeaderProps): React.ReactElement => {
  const theme = useTheme()
  const { isMobile } = useScreen()
  const styles = getStyle(theme)
  const navigation = useNavigation()

  return (
    <View style={styles.container}>
      {iconLeft && (
        // testID "header-icon-left" gives e2e flows a stable selector for the
        // header's back/left action icon (the right icon has the mirror testID).
        <Icon
          testID="header-icon-left"
          name={iconLeft}
          onPress={onPressLeft || (() => navigation.goBack())}
          color={iconColor || theme.colors.black}
        />
      )}

      <Text
        type={isMobile ? 'titleBold' : 'titleBigBold'}
        style={!iconLeft ? styles.titleLeftAlign : styles.title}
      >
        {title}
      </Text>

      {right ? (
        right
      ) : iconRight ? (
        // testID "header-icon-right" gives e2e flows a stable selector for the
        // header's right-side action icon (e.g. "+" to add a category/hashtag/note).
        <Icon
          testID="header-icon-right"
          name={iconRight}
          onPress={onPressRight}
          color={iconColor || theme.colors.black}
        />
      ) : (
        <View style={styles.iconPlaceholder} />
      )}
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.xl,
      paddingBottom: Platform.OS === 'web' ? theme.spacing.lg : theme.spacing.sm,
      paddingTop: Platform.OS === 'web' ? theme.spacing.lg : 0,
    },
    title: {
      flex: 1,
      textAlign: 'center',
    },
    titleLeftAlign: {
      flex: 1,
      textAlign: 'left',
    },
    iconPlaceholder: {
      width: theme.fontSize.extraLarge,
    },
  })
